import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import expenseService from '../../services/ExpenseService.js';
import { IndianRupee, Pencil, ArrowLeft, FileText, X } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { formatRelativeTime, formatIndianDateTime } from '../../utils/localDate.js';

const hasFilledValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return !Number.isNaN(value);
  const text = String(value).trim();
  if (!text) return false;
  if (text.toUpperCase() === 'N/A') return false;
  return true;
};

function InfoField({ label, value, className = '', valueClassName = '' }) {
  if (!hasFilledValue(value)) return null;
  return (
    <div className={className}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      <p className={`font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-words ${valueClassName}`}>
        {value}
      </p>
    </div>
  );
}

function ValueBox({ label, value, boxClassName = '', valueClassName = '' }) {
  if (!hasFilledValue(value)) return null;
  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-xl p-3 min-w-0 ${boxClassName}`}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      <p className={`font-medium text-gray-700 text-sm sm:text-base mt-1 break-words ${valueClassName}`}>
        {value}
      </p>
    </div>
  );
}

function SectionCard({ title, children, className = 'mb-4 sm:mb-6' }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 sm:p-5 ${className}`}>
      <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        {title}
      </h2>
      {children}
    </div>
  );
}

const ViewExpense = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canReview = ['System Admin', 'Admin', 'Accountant'].includes(user?.role);
  const { expenseId } = useParams();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionStatus, setDecisionStatus] = useState('approved');
  const [decisionNote, setDecisionNote] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExpense();
  }, [expenseId]);

  const fetchExpense = async () => {
    try {
      const response = await expenseService.getById(expenseId);
      setExpense(response.data);
    } catch (err) {
      Alert.error('Failed to load expense details');
      console.error(err);
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const getFileAttachmentUrl = () => {
    return expense?.attachment_url || null;
  };

  const openFileModal = () => {
    setIsDocModalOpen(true);
  };

  const closeFileModal = () => {
    setIsDocModalOpen(false);
  };

  const submitDecision = async () => {
    const note = decisionNote.trim();
    if (!note) {
      Alert.error('Add a note for this update');
      return;
    }
    try {
      setDecisionSaving(true);
      await expenseService.decide(expenseId, decisionStatus, note);
      Alert.success(decisionStatus === 'approved' ? 'Expense approved' : 'Expense rejected');
      setDecisionOpen(false);
      setDecisionNote('');
      fetchExpense();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update expense status');
    } finally {
      setDecisionSaving(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-50 text-yellow-800 border-yellow-200',
      approved: 'bg-blue-50 text-blue-800 border-blue-200',
      paid: 'bg-green-50 text-green-800 border-green-200',
      rejected: 'bg-red-50 text-red-800 border-red-200',
      cancelled: 'bg-gray-50 text-gray-700 border-gray-200'
    };
    return statusClasses[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return null;
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading expense details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!expense) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <IndianRupee className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold text-base sm:text-lg">Expense not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const paymentModeDisplay = expense.payment_mode
    ? expense.payment_mode.replace(/_/g, ' ').toUpperCase()
    : null;
  const locationHubId = expense.location_hub_business_id || expense.location_hub_id;
  const isSystemGenerated = ['salary', 'ml_commission', 'ml_commission_deduct'].includes(expense.source_type);

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
              <IndianRupee className="w-6 h-6 text-buttonhoverTxt shrink-0" />
              Expense Details
            </h1>
          </div>
          <div className="flex flex-col gap-2 w-full lg:w-auto sm:flex-row sm:flex-wrap sm:gap-3">
            {canReview && !isSystemGenerated && expense.status === 'pending' && (
              <Button variant="success" onClick={() => { setDecisionStatus('approved'); setDecisionOpen(true); }}>
                Open request
              </Button>
            )}
            <Button variant="secondary" onClick={() => setStatusOpen(true)}>
              Expense status
            </Button>
            {!isSystemGenerated && (
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => navigate(`/expenses/edit/${expenseId}`)}
            >
              Edit
            </Button>
            )}
            <Button
              variant="secondary"
              icon={ArrowLeft}
              mobileIconOnly
              mobileLabel="Back to Expenses"
              onClick={() => navigate('/expenses')}
            >
              Back to Expenses
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-4 sm:mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500">
              <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                Expense ID
              </p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600 font-mono break-all">
                {expense.expense_id}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500">
              <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                Amount
              </p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">
                {formatAmount(expense.amount)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-blue-500">
              <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                Status
              </p>
              <span
                className={`inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase border ${getStatusBadgeClass(
                  expense.status
                )}`}
              >
                {expense.status_display || expense.status}
              </span>
            </div>

            <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-amber-500">
              <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                Category
              </p>
              <p className="text-lg sm:text-xl font-bold text-amber-700 break-words">
                {expense.category_display || '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Basic + Vendor */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6 min-w-0">
          <SectionCard title="Basic Information" className="mb-0 min-w-0">
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-5">
              <InfoField label="Expense ID" value={expense.expense_id} />
              <InfoField label="Category" value={expense.category_display} />
              <InfoField label="Shipment" value={expense.shipment_id || '—'} />
              <InfoField label="Month/Year" value={expense.month_year} />
              <InfoField label="Expense Date" value={formatDate(expense.expense_date)} />
              <InfoField label="Amount" value={formatAmount(expense.amount)} />
              <InfoField label="Location Hub Name" value={expense.location_hub_name} />
              <InfoField label="Location Hub ID" value={locationHubId} />
            </div>
            <InfoField
              label="Description"
              value={expense.description}
              className="mt-4 sm:mt-5"
            />
          </SectionCard>

          <SectionCard title="Vendor Information" className="mb-0 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
              <InfoField label="Vendor Name" value={expense.vendor_name} />
              <InfoField label="Bill Number" value={expense.bill_number} />
            </div>
            {!hasFilledValue(expense.vendor_name) && !hasFilledValue(expense.bill_number) && (
              <p className="text-sm text-gray-500">No vendor details available</p>
            )}
          </SectionCard>
        </div>

        {/* Payment Information */}
        {expense.status === 'paid' && (
          <SectionCard title="Payment Information">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              <ValueBox label="Payment Mode" value={paymentModeDisplay} />
              <ValueBox label="Payment Date" value={formatDateTime(expense.payment_date)} />
              <ValueBox label="Transaction ID" value={expense.transaction_id} />
              <ValueBox label="Paid To" value={expense.paid_to} />
              <ValueBox label="Paid By" value={expense.paid_by} />
            </div>
          </SectionCard>
        )}

        {/* Approval Information */}
        {expense.status === 'approved' && (
          <SectionCard title="Approval Information">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              <ValueBox label="Approved By" value={expense.approved_by} />
              <ValueBox label="Approved At" value={formatDateTime(expense.approved_at)} />
            </div>
          </SectionCard>
        )}

        {/* Additional + System */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6 min-w-0">
          <SectionCard title="Additional Information" className="mb-0 min-w-0">
            <div className="space-y-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">File Attachment</p>
                {expense.has_attachment ? (
                  <Button variant="primary" icon={FileText} onClick={openFileModal}>
                    View Attachment
                  </Button>
                ) : (
                  <p className="font-medium text-gray-500 text-sm sm:text-base bg-gray-100 p-2 py-1">
                    No attachment available
                  </p>
                )}
              </div>
              <InfoField label="Remarks" value={expense.remarks || 'No remarks'} />
            </div>
          </SectionCard>

          <SectionCard title="System Information" className="mb-0 min-w-0">
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-5">
              <InfoField label={t('common.createdBy')} value={expense.created_by_name || expense.created_by} />
              <InfoField label="Created At" value={`${formatRelativeTime(expense.created_at)} · ${formatDateTime(expense.created_at)}`} />
              <InfoField label="Decision note" value={expense.status_note} />
              <InfoField label="Last Updated" value={formatDateTime(expense.updated_at)} />
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Status</p>
                <span
                  className={`inline-block mt-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold uppercase border ${getStatusBadgeClass(
                    expense.status
                  )}`}
                >
                  {expense.status_display || expense.status}
                </span>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {statusOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-sky-950/40 p-3 sm:p-4" onClick={() => setStatusOpen(false)}>
          <div className="w-full sm:max-w-md bg-white rounded-2xl shadow-xl border border-sky-100 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-sky-50 border-b border-sky-100 px-4 py-3 flex items-start justify-between">
              <h2 className="text-lg font-semibold text-sky-950">Expense status</h2>
              <button type="button" onClick={() => setStatusOpen(false)} aria-label="Close"><X className="w-5 h-5 text-sky-700" /></button>
            </div>
            <div className="p-4 sm:p-5 space-y-3 text-sm">
              <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Requested</p>
                <p className="font-semibold text-sky-950 mt-1">{expense.created_by_name || expense.created_by}</p>
                <p className="text-sky-900">{formatRelativeTime(expense.created_at)}</p>
                <p className="text-xs text-sky-700/80">{formatIndianDateTime(expense.created_at)}</p>
              </div>
              <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                  {expense.status === 'rejected' ? 'Rejected' : expense.status === 'approved' ? 'Approved' : 'Decision'}
                </p>
                {expense.approved_at ? (
                  <>
                    <p className="font-semibold text-sky-950 mt-1">{expense.approved_by}</p>
                    <p className="text-sky-900">{formatRelativeTime(expense.approved_at)}</p>
                    <p className="text-xs text-sky-700/80">{formatIndianDateTime(expense.approved_at)}</p>
                    <p className="mt-2 rounded-lg bg-white border border-sky-100 px-2.5 py-2 text-sky-950 break-words">{expense.status_note || 'No note'}</p>
                  </>
                ) : (
                  <p className="mt-1 text-sky-900">Still pending.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {decisionOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-sky-950/40 p-3 sm:p-4">
          <div className="w-full sm:max-w-md bg-white rounded-2xl shadow-xl border border-sky-100 overflow-hidden">
            <div className="bg-sky-50 border-b border-sky-100 px-4 py-3 flex items-start justify-between">
              <h2 className="text-lg font-semibold text-sky-950">Update status</h2>
              <button type="button" onClick={() => setDecisionOpen(false)} aria-label="Close"><X className="w-5 h-5 text-sky-700" /></button>
            </div>
            <div className="p-4 sm:p-5">
            <div className="grid grid-cols-2 gap-2 mb-3">
              {['approved', 'rejected'].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setDecisionStatus(value)}
                            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold capitalize ${decisionStatus === value ? 'border-sky-600 bg-sky-600 text-white' : 'border-sky-200 bg-sky-50 text-sky-900'}`}
                >
                  {value}
                </button>
              ))}
            </div>
            <textarea
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-sky-200 bg-sky-50/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
              placeholder="Note for this update"
            />
            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="secondary" onClick={() => setDecisionOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={submitDecision} disabled={decisionSaving}>
                {decisionSaving ? 'Saving…' : 'Update status'}
              </Button>
            </div>
            </div>
          </div>
        </div>
      )}

      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeFileModal}
        documentUrl={getFileAttachmentUrl()}
        documentType="expense_attachment"
      />
    </DashboardLayout>
  );
};

export default ViewExpense;
